import { useEffect, useState } from 'react';
import { WHATSAPP } from '../../content/site.js';
import WhatsAppIcon from './WhatsAppIcon.jsx';

/**
 * Floating WhatsApp launcher, bottom-right on every page.
 *
 * Sits at z-40: under the sticky header and the lightbox overlay (both z-50)
 * so it never floats over a dialog, but above ordinary page content.
 *
 * Collapsed to a 56px circle by default — the WhatsApp glyph is recognisable
 * enough not to need a permanent label, and a persistent pill would cover more
 * of the page on phones, where screen space is scarcest. On pointer devices it
 * expands to reveal the label on hover or keyboard focus.
 */
export default function WhatsAppWidget() {
  const [shown, setShown] = useState(false);

  /* Held back until the reader has scrolled past roughly the first screen.
     Pinned bottom-right from the start it lands on top of the hero's own
     buttons on a phone — covering a primary CTA to offer a secondary one.
     Appearing on scroll also matches the behaviour people already expect
     from chat launchers. Pages too short to scroll just show it after a
     beat, otherwise it could never appear there at all. */
  useEffect(() => {
    const threshold = () => window.innerHeight * 0.6;
    const scrollable = () => document.documentElement.scrollHeight > window.innerHeight + 120;

    if (!scrollable()) {
      const t = window.setTimeout(() => setShown(true), 600);
      return () => window.clearTimeout(t);
    }

    const onScroll = () => setShown(window.scrollY > threshold());
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <a
      href={WHATSAPP.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat with Chickrah on WhatsApp"
      data-testid="whatsapp-widget"
      className={[
        'group fixed right-4 z-40 flex items-center rounded-full bg-[#25D366]',
        'shadow-[0_8px_24px_rgba(0,0,0,0.28)] ring-1 ring-black/5',
        'transition-all duration-300 ease-out hover:bg-[#1EBE5B] hover:shadow-[0_12px_30px_rgba(0,0,0,0.34)]',
        'focus-visible:ring-2 focus-visible:ring-amber focus-visible:ring-offset-2',
        'motion-reduce:transition-none sm:right-6',
        // Clears the iOS home indicator instead of sitting under it.
        'bottom-[calc(1rem+env(safe-area-inset-bottom))] sm:bottom-[calc(1.5rem+env(safe-area-inset-bottom))]',
        shown ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-3 opacity-0',
      ].join(' ')}
    >
      <span className="grid h-14 w-14 shrink-0 place-items-center">
        <WhatsAppIcon size={27} className="text-white" />
      </span>

      {/* Expands on hover/focus. max-width rather than width so the label can
          size to its own text, and so the collapsed state costs no layout. */}
      <span
        className={[
          'max-w-0 overflow-hidden whitespace-nowrap font-semibold text-white',
          'text-[14px] leading-none transition-all duration-300 ease-out',
          'motion-reduce:transition-none',
          'group-hover:max-w-[12rem] group-hover:pr-5',
          'group-focus-visible:max-w-[12rem] group-focus-visible:pr-5',
        ].join(' ')}
      >
        Chat with us
      </span>
    </a>
  );
}
